import { useState } from 'react';
import { Trash2, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useMembers } from '@/features/members/queries';
import { pageMembersHooks } from '../queries';
import type { WikiPage, WikiPageMember, WikiPagePermission } from '../types';

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

/** Single-page sharing (WikiPageMember) — org-Member only, no public links. */
export function SharePageDialog({
  page,
  open,
  onOpenChange,
}: {
  page: WikiPage;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const sharesQuery = pageMembersHooks.useList({ page: page.id }, open);
  const create = pageMembersHooks.useCreate();
  const remove = pageMembersHooks.useRemove();
  const [search, setSearch] = useState('');
  const [permission, setPermission] = useState<WikiPagePermission>('view');
  const membersQuery = useMembers({ search: search || undefined, type: 'staff', is_active: true });

  const shares = rows<WikiPageMember>(sharesQuery.data);
  const sharedMemberIds = new Set(shares.map((s) => s.member));
  const candidates = rows(membersQuery.data).filter((m) => !sharedMemberIds.has(m.id));

  function share(memberId: number) {
    create.mutate(
      { page: page.id, member: memberId, permission },
      { onSuccess: () => setSearch('') },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share “{page.title || 'Untitled'}”</DialogTitle>
          <DialogDescription>
            Give specific people access to this page. Sharing is people-only — there are no public links.
          </DialogDescription>
        </DialogHeader>

        {/* Add row */}
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search teammates…"
            className="h-9"
          />
          <Select value={permission} onValueChange={(v) => setPermission(v as WikiPagePermission)}>
            <SelectTrigger className="h-9 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="view">Can view</SelectItem>
              <SelectItem value="edit">Can edit</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {search ? (
          <div className="max-h-44 space-y-0.5 overflow-y-auto rounded-md border p-1">
            {membersQuery.isPending ? (
              <Skeleton className="h-16 w-full" />
            ) : candidates.length === 0 ? (
              <p className="text-muted-foreground px-2 py-2 text-xs">No matching teammates.</p>
            ) : (
              candidates.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  disabled={create.isPending}
                  onClick={() => share(m.id)}
                  className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
                >
                  <UserPlus className="text-muted-foreground size-4" />
                  <span className="truncate">{m.user.full_name || m.user.email}</span>
                </button>
              ))
            )}
          </div>
        ) : null}

        {/* Current shares */}
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium">People with access</p>
          {sharesQuery.isPending ? (
            <Skeleton className="h-12 w-full" />
          ) : shares.length === 0 ? (
            <p className="text-muted-foreground py-2 text-sm">Not shared with anyone yet.</p>
          ) : (
            <ul className="divide-y">
              {shares.map((s) => (
                <li key={s.id} className="flex items-center gap-2 py-2 text-sm">
                  <span className="flex-1 truncate">{s.member_name || `Member #${s.member}`}</span>
                  <span className="text-muted-foreground text-xs capitalize">{s.permission}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label="Remove access"
                    onClick={() => remove.mutate(s.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
